import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Undo2, Receipt, AlertTriangle, Loader2, Link2Off } from "lucide-react";
import { apiClient } from "@/lib/apiClient";
import { DocumentViewer } from "@/components/inbox/DocumentViewer";

const chf = (v) => Number(v || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function useBlob(path) {
  const [state, setState] = useState({ url: null, type: "", error: false });
  useEffect(() => {
    if (!path) { setState({ url: null, type: "", error: true }); return undefined; }
    let alive = true, url = null;
    setState({ url: null, type: "", error: false });
    apiClient.get(path, { responseType: "blob" }).then((r) => {
      if (!alive) return;
      url = URL.createObjectURL(r.data);
      setState({ url, type: r.data.type || "", error: false });
    }).catch(() => alive && setState({ url: null, type: "", error: true }));
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [path]);
  return state;
}

function PreviewPane({ title, subtitle, path, fileName, testid, empty, toolbar }) {
  const { url, type, error } = useBlob(path);
  return (
    <div className="flex flex-col h-[62vh] min-h-[22rem] rounded-lg border border-slate-200 overflow-hidden bg-white" data-testid={testid}>
      <div className="px-3 py-1.5 border-b border-slate-100 bg-slate-50">
        <div className="text-xs font-medium text-slate-800 truncate">{title}</div>
        {subtitle && <div className="text-[11px] text-slate-500 truncate">{subtitle}</div>}
        {toolbar && <div className="mt-1">{toolbar}</div>}
      </div>
      <div className="flex-1 min-h-0">
        {empty ? (
          <div className="h-full flex flex-col items-center justify-center text-slate-400 text-xs gap-1 p-4 text-center"><Link2Off className="h-5 w-5" />{empty}</div>
        ) : error ? (
          <div className="h-full flex items-center justify-center text-slate-400 text-xs">Vorschau nicht verfügbar</div>
        ) : !url ? (
          <div className="h-full flex items-center justify-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          <DocumentViewer blobUrl={url} contentType={type} fileName={fileName} fitMode="receipt" />
        )}
      </div>
    </div>
  );
}

/** Zweite Bestätigung für Gutschriften: Gutschrift und gefundener Kaufbeleg nebeneinander – Geld zurück oder doch bezahlt? */
export function CreditNoteConfirmDialog({ companyId, item, onConfirmCredit, onConfirmNormal, onCancel }) {
  const ex = item?.extracted || {};
  const cands = useMemo(() => item?.credit_origin_candidates || [], [item]);
  const savedId = ex.credit_of_receipt_id || "";
  const [selId, setSelId] = useState(savedId || (cands[0]?.id ?? ""));
  const [orig, setOrig] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setSelId(savedId || (cands[0]?.id ?? "")); }, [savedId, item?.id, cands]);
  useEffect(() => {
    setOrig(null);
    if (!selId) return undefined;
    let alive = true;
    apiClient.get(`/receipts/${selId}`).then((r) => alive && setOrig(r.data)).catch(() => alive && setOrig(false));
    return () => { alive = false; };
  }, [selId]);
  if (!item) return null;
  const origId = selId;
  const selCand = cands.find((c) => c.id === selId);
  const auto = ex.credit_origin_auto;
  const confirmCredit = async () => {
    if (selId !== savedId) {
      setSaving(true);
      try { await apiClient.post(`/companies/${companyId}/inbox/${item.id}/credit-note`, { is_credit_note: true, original_receipt_id: selId || "" }); }
      catch { /* Verknüpfung optional */ }
      finally { setSaving(false); }
    }
    onConfirmCredit?.();
  };
  const amount = chf(Math.abs(Number(ex.total_amount || 0)));
  const cur = ex.currency || "CHF";
  const diff = orig ? Math.abs(Number(orig.amount || 0)) - Math.abs(Number(ex.total_amount || 0)) : null;
  return (
    <Dialog open onOpenChange={(o) => !o && onCancel?.()}>
      <DialogContent className="max-w-[96vw] xl:max-w-[1500px] max-h-[96vh] overflow-y-auto" data-testid="credit-note-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            Wirklich eine Gutschrift?
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <PreviewPane title={`Gutschrift · ${ex.supplier_name || item.file_name || "Beleg"}`} subtitle={`${cur} ${amount}${ex.invoice_date ? ` · ${new Date(ex.invoice_date).toLocaleDateString("de-CH")}` : ""}`}
            path={`/companies/${companyId}/inbox/${item.id}/file`} fileName={item.file_name} testid="credit-note-preview-credit" />
          <PreviewPane
            title={orig ? `Kaufbeleg · ${orig.description || "Beleg"}` : "Kaufbeleg"}
            subtitle={orig ? `${orig.currency || "CHF"} ${chf(orig.amount)}${orig.date ? ` · ${new Date(orig.date).toLocaleDateString("de-CH")}` : ""}${diff != null && Math.abs(diff) > 0.005 ? ` · Differenz ${chf(diff)}` : " · gleicher Betrag"}${selCand?.items_matched?.length ? ` · Artikel ${selCand.items_matched.join(", ")} auf dem Kauf` : auto?.items?.length && selId === savedId ? ` · ${auto.reason}` : " · gleicher Händler, vor der Rückgabe"}` : null}
            path={orig ? `/receipts/${orig._id}/file` : null} fileName={orig?.file_name}
            empty={!origId ? "Kein passender Kaufbeleg desselben Händlers vor der Rückgabe gefunden – bitte besonders prüfen, ob es wirklich eine Gutschrift ist." : orig === false ? "Kaufbeleg nicht mehr vorhanden" : null}
            testid="credit-note-preview-original"
            toolbar={(cands.length > 0 || savedId) && (
              <select value={selId} onChange={(e) => setSelId(e.target.value)} className="h-7 max-w-full rounded-md border border-slate-300 bg-white px-1.5 text-[11px] text-slate-800" data-testid="credit-note-origin-select">
                <option value="">– kein Kaufbeleg verknüpfen –</option>
                {savedId && !cands.some((c) => c.id === savedId) && <option value={savedId}>{ex.credit_of_receipt_label || "verknüpfter Kaufbeleg"}</option>}
                {cands.map((c) => (
                  <option key={c.id} value={c.id}>{c.items_matched?.length ? "✓ " : ""}{c.description} · {c.date ? new Date(c.date).toLocaleDateString("de-CH") : ""} · {chf(c.amount)}{c.exact_amount ? " · gleicher Betrag" : ""}</option>
                ))}
              </select>
            )} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-slate-700">
          <div className="rounded-lg border border-teal-200 bg-teal-50 p-3 text-teal-900">
            <div className="font-medium">Gutschrift = Geld kommt zurück</div>
            <div className="text-xs mt-0.5">Rückgabe, Retoure oder Rückerstattung des Händlers. Es wird <b>kein Aufwand</b> gebucht – der Kaufbeleg wird reduziert.</div>
            {ex.credit_note_hint && <div className="text-xs mt-1 text-amber-800">{ex.credit_note_hint}</div>}
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="font-medium">Normaler Beleg = wir haben bezahlt</div>
            <div className="text-xs mt-0.5 text-slate-600">Du hast dem Händler Geld überwiesen oder mit Karte bezahlt → Aufwand buchen.</div>
          </div>
        </div>
        <div className="flex flex-wrap justify-end gap-2 pt-2 border-t border-slate-100">
          <Button variant="ghost" onClick={onCancel} data-testid="credit-note-confirm-cancel">Abbrechen</Button>
          <Button variant="outline" onClick={onConfirmNormal} data-testid="credit-note-confirm-normal">
            <Receipt className="h-4 w-4 mr-1" />Nein, wir haben bezahlt – als Beleg
          </Button>
          <Button onClick={confirmCredit} disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="credit-note-confirm-credit">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Undo2 className="h-4 w-4 mr-1" />}Ja, Gutschrift bestätigen{selId ? " & mit Kauf verknüpfen" : ""}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
